import React, { ErrorInfo, PureComponent } from 'react';

import { IntlShape, injectIntl } from 'react-intl';
import styled from 'styled-components';

import { useLogger } from 'state/logger';
import { isProduction } from 'utils/environment';

import Icon from './icon';

const CenteredText = styled.div`
  text-align: center;
  svg {
    fill: ${Styles.color.primary};
    width: 10rem;
    height: 10rem;
  }
`;

interface IErrorBoundaryProps {
  logger: ReturnType<typeof useLogger>;
  intl: IntlShape;
}

type ErrorBoundaryState = { error: Error; info: ErrorInfo } | { error: null; info: null };

class InternalErrorBoundary extends PureComponent<IErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    error: null,
    info: null,
  };

  static getDerivedStateFromError(error: Error, info: ErrorInfo) {
    return { error, info };
  }

  componentDidUpdate(_: IErrorBoundaryProps, prevState: ErrorBoundaryState) {
    const { error } = this.state;
    if (error && prevState.error !== error) {
      this.props.logger.error(error);
    }
  }

  render() {
    if (this.state.error) {
      // replace with custom fallback UI
      return (
        <CenteredText>
          <h2>{this.props.intl.formatMessage({ id: 'somethingWrong' })}</h2>
          <Icon />
          {!isProduction ? (
            <details>
              <summary>{this.state.error.message}</summary>
              {this.state.info.componentStack || this.state.error.stack}
            </details>
          ) : null}
        </CenteredText>
      );
    }
    return this.props.children;
  }
}

const ErrorBoundary: React.FC<{ intl: IntlShape }> = ({ children, intl }) => {
  const logger = useLogger();
  return (
    <InternalErrorBoundary intl={intl} logger={logger}>
      {children}
    </InternalErrorBoundary>
  );
};

export default injectIntl(ErrorBoundary);
